import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { Loader2 } from 'lucide-react';
import { cn } from '../cn';

const buttonVariants = cva(
  'inline-flex items-center justify-center gap-1.5 whitespace-nowrap font-medium select-none transition-[background-color,color,transform,box-shadow] duration-[var(--duration)] ease-[var(--ease)] active:scale-[0.98] disabled:opacity-50 disabled:active:scale-100',
  {
    variants: {
      variant: {
        primary: 'bg-accent text-white hover:bg-accent-hover shadow-[0_1px_2px_rgba(0,0,0,0.12)]',
        secondary: 'bg-surface text-text shadow-[var(--shadow-sm)] hover:bg-surface-2',
        tinted: 'bg-accent-tint text-accent hover:bg-[color-mix(in_srgb,var(--accent)_18%,transparent)]',
        plain: 'bg-transparent text-accent hover:bg-fill-3',
        ghost: 'bg-transparent text-text-2 hover:bg-fill-3 hover:text-text',
        destructive: 'bg-red text-white hover:brightness-110 shadow-[0_1px_2px_rgba(0,0,0,0.12)]',
        'destructive-tinted': 'bg-red-tint text-red hover:bg-[color-mix(in_srgb,var(--red)_18%,transparent)]',
      },
      size: {
        sm: 'h-7 px-3 text-[13px] rounded-full',
        md: 'h-9 px-4 text-[14px] rounded-[var(--radius-sm)]',
        lg: 'h-11 px-5 text-[15px] rounded-[var(--radius)]',
      },
    },
    defaultVariants: { variant: 'secondary', size: 'md' },
  },
);

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
  loading?: boolean;
  icon?: React.ReactNode;
  trailingIcon?: React.ReactNode;
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { className, variant, size, loading, icon, trailingIcon, children, disabled, type = 'button', ...props },
  ref,
) {
  return (
    <button ref={ref} type={type} className={cn(buttonVariants({ variant, size }), className)} disabled={disabled || loading} aria-busy={loading || undefined} {...props}>
      {loading ? <Loader2 className="ui-spin size-4" aria-hidden /> : icon ? <span className="[&>svg]:size-4 -ml-0.5 inline-flex">{icon}</span> : null}
      {children}
      {trailingIcon ? <span className="[&>svg]:size-4 -mr-0.5 inline-flex">{trailingIcon}</span> : null}
    </button>
  );
});

export { buttonVariants };

export interface IconButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  label: string;
  size?: 'sm' | 'md' | 'lg';
  variant?: 'plain' | 'tinted' | 'secondary' | 'ghost';
}

export const IconButton = React.forwardRef<HTMLButtonElement, IconButtonProps>(function IconButton({ label, size = 'md', variant = 'ghost', className, children, type = 'button', ...props }, ref) {
  const sizes = { sm: 'size-7 [&>svg]:size-3.5 rounded-full', md: 'size-8 [&>svg]:size-4 rounded-[var(--radius-sm)]', lg: 'size-10 [&>svg]:size-5 rounded-[var(--radius)]' };
  const variants = {
    plain: 'text-accent hover:bg-fill-3',
    tinted: 'bg-accent-tint text-accent',
    secondary: 'bg-surface text-text shadow-[var(--shadow-sm)] hover:bg-surface-2',
    ghost: 'text-text-2 hover:bg-fill-3 hover:text-text',
  };
  return (
    <button ref={ref} type={type} aria-label={label} title={label} className={cn('inline-flex items-center justify-center transition-colors duration-[var(--duration)] active:scale-95 disabled:opacity-50', sizes[size], variants[variant], className)} {...props}>
      {children}
    </button>
  );
});
